/* Dynamic Plot code copyright 2015 (c) Tuva Labs Inc. */

import { PlotElement } from "./plotElement";

/**
 * Dyn.MixupElement()
 * The Mixup Element is responsible for controlling the case icon positions when in the Reset state.
 * It is called "Mixup" based on TinkerPlots "mixup" state which it mimics.
 * @constructor
 */
class MixupElement extends PlotElement {

    constructor( Dyn, hint ) {
        super( Dyn );
        this.initElement({
            priority: Dyn.EPlotPriority.kPriorityMixup,
            replaceType: Dyn.EPlotControl.kControlsShape,
            primaryType: Dyn.EPlotControl.kControlsMixup,
            otherControls: []
        }, Dyn );

        // random virtual positions in [0.0-1.0] range.
        // Note: to allow user to move these, we must update these positions on drag, then save/restore
        this.randomX = [];
        this.randomY = [];
        this.resetUserPlotTitle = false;
        this.needsLegendReset = false;
    }

    reset() {
        // Set flags for when parent object next calls this.calcAxis(), to reset the Plot Title and Legend
        //      do not reset legend or plot title during other updates,
        //      e.g. user can save/restore a custom plot title with mixup
        //      e.g. user can resize the plot window and the custom plot title will be preserved.
        this.resetUserPlotTitle = true; // reset the user title only when the Reset button is pressed (otherwise allow save of mixup with custom title)
        this.needsLegendReset = true;   // reset the legend only when Reset button is pressed
    }

    // get kDefaultTitle() { return Dyn.plotTitles.MIXED_UP_CASES; }
    // get kUndoAction()   { return 'Reset'; }

    toString() {
        return '{MixupElement}';
    }

    save() {
        var Dyn = this.dyn,
            plotElementModel = super.save();
        if( !Dyn.plotUtil.isDrawOnly() ) {
            plotElementModel.randomX = this.randomX.map( n => toScreenPrecision( n ) );
            plotElementModel.randomY = this.randomY.map( n => toScreenPrecision( n ) );
        }

        Dyn.assert(!plotElementModel.resetUserPlotTitle, "Don't save 'resetUserPlotTitle'");
        Dyn.assert(!plotElementModel.needsLegendReset, "Don't save 'needsLegendReset'");
        return plotElementModel;

        function toScreenPrecision(n) {  // save space, 15 digits of precision not needed
            return Dyn.mathUtil.roundToPrecision(n, 6);
        }
    }

    restore(savedData) {
        let Dyn = this.dyn;
        function copyXorYposition(savedArray, thisArray) {
            if (savedArray && Array.isArray(savedArray)) {
                thisArray.length = 0;
                savedArray.forEach(n => {
                    thisArray.push(Number.isFinite(n) ? n : Math.random());
                });
            }
        }

        super.restore( savedData );
        if( !Dyn.plotUtil.isDrawOnly() ) {
            copyXorYposition( savedData.randomX, this.randomX );
            copyXorYposition( savedData.randomY, this.randomY );
        }

        // clear these flags, used for Reset, not save/restore (see assert false in .save())
        this.needsLegendReset = Boolean(savedData.needsLegendReset);
        this.resetUserPlotTitle = Boolean(savedData.resetUserPlotTitle);
    }

    getPlotSummary() {
        let Dyn = this.dyn;
        return Dyn.plotUtil.mixupPlotSummary;
    }

    /**
     * Return true if this (new) plot element should replace the given existing plot element.
     */
    replaces( iExistingPlotElement, ioPlotAdornments ) {
        // remove this code which was added 1/6/24 for #2146 Draw Only Mode; instead use soft reset #2303
        // let Dyn = this.dyn;
        // if( Dyn.plotUtil.isDrawOnly() &&  (iExistingPlotElement.replaceType === Dyn.EPlotControl.kControlsAnnotations)) {
        //     let savedAnnotationLayer = null;
        //     console.log("TO DO: restore the original saved data, instead of deleting the annotations.");
        //     return iExistingPlotElement.resetForDrawMode(savedAnnotationLayer);
        // }
        return true; // remove all other plot elements to reset plot
    }

    /** Update the plot's axis model, removing any elements incompatible with this PlotElement. */
    calcAxis(ioWhatChanged, subViewRef) {
        let Dyn = this.dyn,
            xAxisView = subViewRef.xAxisView, yAxisView = subViewRef.yAxisView,
            isDrawOnly = Dyn.plotUtil.isDrawOnly();

        // for mixup, we want to remove all attributes and clear all titles.
        // The subroutines will update 'ioWhatChanged' so that views can be adjusted appropriately.
        if (xAxisView.isEmptyOfAttributes()) {
            xAxisView.setTitle( isDrawOnly ? "" : Dyn.loc( Dyn.plotUtil.isTuvaJr() ? Dyn.plotStrings.promptJr : Dyn.plotStrings.promptX ));
        } else {
            xAxisView.removeAttribute(Dyn.loc( Dyn.plotUtil.isTuvaJr() ? Dyn.plotStrings.promptJr : Dyn.plotStrings.promptX ), ioWhatChanged);
        }

        if (yAxisView.isEmptyOfAttributes()) {
            yAxisView.setTitle( isDrawOnly ? "" : Dyn.loc( Dyn.plotUtil.isTuvaJr() ? Dyn.plotStrings.promptJr : Dyn.plotStrings.promptY ));
        } else {
            yAxisView.removeAttribute(Dyn.loc( Dyn.plotUtil.isTuvaJr() ? Dyn.plotStrings.promptJr : Dyn.plotStrings.promptY ), ioWhatChanged);
        }

        this.plotShaper.setDefaultPlotTitle( subViewRef.topMargin, Dyn.loc(this.kDefaultTitle), this.resetUserPlotTitle);
        this.resetUserPlotTitle = false; // do only once on reset, not on recalc of a mixup plot

        // clear axis settings from other shapers
        xAxisView.setPlotSpecificOptions(null);
        yAxisView.setPlotSpecificOptions(null);
        subViewRef.topMargin.setPlotSpecificOptions(null);
        subViewRef.rightMargin.setPlotSpecificOptions(null);
        // To reset legend area
        if (this.needsLegendReset) {
            // do on Reset button, not on every update to mixup plot
            subViewRef.rightMargin.reset();
            this.needsLegendReset = false;
        }
    }

    /**
     * Update the allowable number of case icons show per data case, normally 1.
     * PlotElements that control the plot shape can override this function, and return a 1+ value.
     */
    calcNumIconsPerCase( ioWhatChanged, subViewRef ) {
        let Dyn = this.dyn;
        return Dyn.plotUtil.isDrawOnly() ? 0 :  1; // hide all case icons if in draw-only mode
    }

    /**
     * Record which cases that have missing values for the given plot.
     */
    calcMissingValueCases(numCases, xAxisView, yAxisView) {
        this.plotShaper.dataSet.missingCases.clearAllMissingCases();
    }

    /** update the case positional data (shared by all elements) and adornments (specific to this element) */
    calcPosition(ioPositions, xAxisView, yAxisView, ioAdornments) {
        let Dyn = this.dyn,
            iconStyler = this.plotShaper.iconStyler,
            dataSet = this.plotShaper.dataSet,
            numPositions = ioPositions.length,
            i, p, x, y;

        // calculate the random position of each case icon in the plot
        // NOTE: the axis views expect a "data" value in [0-1] range if no attribute is present.
        for (i = 0; i < numPositions; i++) {
            if (i >= this.randomX.length) {
                // in the absence of a seed for Math.random() we save the random values,
                // so a recalculated plot will not move to a new random position.
                // To mix up again, just add a new Dyn.MixupElement() to the plot, to replace these old values.
                this.randomX.push(Math.random());
                this.randomY.push(Math.random());
            }
            p = ioPositions[i];
            p.x = xAxisView.dataToPixel(this.randomX[i]);
            p.y = yAxisView.dataToPixel(this.randomY[i]);
            p.r = this.plotShaper.iconStyler.radius;
            p.isVisible = !dataSet.isCaseFilteredOut(p.caseIndex);	// no missing cases
            p.caseIndex = i;
            p.xAttrIndex = 0;
            p.yAttrIndex = 0;
            this.calcBinIndex(p, false, false); // all cases in one bin
            setPositionIfMissingOrFiltered(p); // position all invisible cases offscreen
        }
        this.populateCellsWithCases(ioPositions);
        this.orderCasesInCell( this.plotShaper.bins.cells[0] );

        /**
         * set the position of hidden cases to offscreen (to be animated on screen when status changes)
         * @param p {isVisible,x,y,r} -- case icon position parameters
         */
        function setPositionIfMissingOrFiltered(p) {
            if (!(p.isVisible)) {
                p.x = iconStyler.missingValuePosition.x;
                p.y = iconStyler.missingValuePosition.y;
                p.r = Dyn.plotUtil.missingCaseSize;
            }
        }
    }

    /**
     * Update one case icon position (e.g. if the case was dragged when mixed up)
     * @param iconIndex
     * @param dataX
     * @param dataY
     * @returns {*} -- original position for undo/redo
     */
    updateOnePosition(iconIndex, dataX, dataY) {
        let Dyn = this.dyn;
        Dyn.assertArrayIndex( this.randomX, iconIndex );
        Dyn.assertArrayIndex( this.randomY, iconIndex );
        if( iconIndex < this.randomX.length && iconIndex < this.randomY.length ){
            let oldX = this.randomX[iconIndex],
                oldY = this.randomY[iconIndex];
            this.randomX[iconIndex] = dataX;
            this.randomY[iconIndex] = dataY;
            //console.log( "MixupElement.updatePosition", iconIndex, dataX, dataY );
            return { iconIndex: iconIndex, x: oldX, y: oldY };
        } else {
            return null;
        }
    }

    // re-order the case index array in the plot, to change the tab order for keyboard navigation
    orderCasesInCell( cell ) {
        let Dyn = this.dyn;
        if( Dyn.plotUtil.enableCaseTabOrder ) {
            // get the sort values for each icon
            let sortValues = cell.cases.map( ( positionIndex ) => ({
                    index: positionIndex, // same as case icon index; iconIndex !== caseIndex when multiple attributes
                    value: 1 - this.randomY[positionIndex],  // primary sort: top to bottom
                    value2: this.randomX[positionIndex]  // secondary sort: left to right
                }));
            Dyn.dataUtil.sortCaseIndices( sortValues, cell.cases );
        }
    }
} // class MixupElement

export const wrapDynMixupElement = function(Dyn) {
    Dyn.MixupElement = MixupElement;
    Dyn.MixupElement.prototype.kDefaultTitle = Dyn.plotTitles.MIXED_UP_CASES;
    Dyn.MixupElement.prototype.kUndoAction = 'Reset';
    Dyn.PlotElemHelper.registerType( Dyn.EPlotControl.kControlsMixup, Dyn.MixupElement );
}; // wrapDynMixupElement
